/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import orderPlanService from 'service/orderPlan';
import usersService from 'service/users';
import { useTranslation } from 'react-i18next';
import Flatpickr from 'react-flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru';
import classNames from 'classnames';

const getFlatpickrLocale = () => Russian;

const CreateOrderPlan = ({
  setOpenOrderPlan,
  selectedOrderPlanId,
  refetch,
  setSelectedOrderPlanId
}: any) => {
  const { t } = useTranslation();

  const [chargers, setChargers] = useState<{ pid: number; name: string }[]>([]);
  const [recipients, setRecipients] = useState<{ pid: number; name: string }[]>(
    []
  );

  type FormValues = {
    type: string;
    recipient_pid: number;
    charger_pid: number;
    comment: string;
    photo: string;
    legal_entity_pid: string;
    status_pid: number;
    visit_date: Date[];
  };

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      type: 'PLANNED',
      recipient_pid: 0,
      charger_pid: 0,
      comment: '',
      photo: '',
      legal_entity_pid: '',
      status_pid: 0,
      visit_date: []
    }
  });

  // const statusCodes = [
  //   'CREATED_BY_SUPPLIER',
  //   'CREATED_BY_CHARGER',
  //   'SUBMITTED_BY_CHARGER',
  //   'CANCELLED_BY_CHARGER',
  //   'CONFIRMED_BY_RECIPIENT',
  //   'REJECTED_BY_RECIPIENT',
  //   'CONFIRMED_BY_SUPPLIER',
  //   'REJECTED_BY_SUPPLIER',
  //   'COMPLETED'
  // ];
  useEffect(() => {
    orderPlanService
      .getRecipientList({})
      .then((res: any) => {
        const list = res?.legal_entities || [];

        if (!Array.isArray(list)) {
          console.error('Ожидался массив пользователей, получено:', list);
          return;
        }

        const formatted = list.map((user: any) => ({
          pid: user.pid,
          name: user.name || 'No Name'
        }));

        setRecipients(formatted); // 👈 сохранить
      })
      .catch((error: any) => {
        console.error('Ошибка загрузки получателей:', error);
      });
  }, []);

  useEffect(() => {
    usersService
      .getList({})
      .then((res: any) => {
        const list = res?.data?.users || [];

        if (!Array.isArray(list)) {
          console.error('Ожидался массив пользователей, получено:', list);
          return;
        }

        const formatted = list.map((user: any) => ({
          pid: Number(user.id_number),
          name:
            `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
            'No   Name'
        }));

        setChargers(formatted);
      })
      .catch(error => {
        console.error('Ошибка загрузки пользователей:', error);
      });
  }, []);

  useEffect(() => {
    if (selectedOrderPlanId) {
      orderPlanService.getByPid(selectedOrderPlanId).then((res: any) => {
        const item = res;
        reset({
          type: item?.type || 'PLANNED',
          recipient_pid: item?.recipient_pid || 0,
          charger_pid: item?.charger_pid || 0,
          comment: item?.comment || '',
          photo: item?.photo || '',
          legal_entity_pid: item?.legal_entity_pid?.toString() || '',
          status_pid: item?.status?.pid || 0,
          visit_date: item?.visit_date ? [new Date(item.visit_date)] : []
        });
      });
    } else {
      reset();
    }
  }, [selectedOrderPlanId, reset]);

  const onSubmit = (data: FormValues) => {
    const payload = {
      ...data,
      legal_entity_pid: data.legal_entity_pid
        ? Number(data.legal_entity_pid)
        : undefined,
      status_pid: selectedOrderPlanId ? data.status_pid : 0,
      visit_date: data.visit_date?.[0]
        ? data.visit_date[0].getTime()
        : undefined
    };

    const action = selectedOrderPlanId
      ? orderPlanService.update(selectedOrderPlanId, payload)
      : orderPlanService.create(payload);

    action.then(() => {
      refetch();
      setOpenOrderPlan(false);
      setSelectedOrderPlanId('');
    });
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <input type="hidden" value="PLANNED" {...register('type')} />

      {/* recipient_pid */}
      <Row className="mb-2">
        <Col>
          <Controller
            name="recipient_pid"
            control={control}
            rules={{
              validate: value => value !== 0 || t('recipient_required')
            }}
            render={({ field }) => (
              <Form.Floating>
                <Form.Select
                  {...field}
                  value={field.value}
                  onChange={e => field.onChange(Number(e.target.value))}
                  isInvalid={!!errors.recipient_pid}
                >
                  <option value={0} className="d-none">
                    {''}
                  </option>
                  {recipients.map(user => (
                    <option key={user.pid} value={user.pid}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
                <label>{t('recipient_pid')}</label>
                {errors.recipient_pid && (
                  <Form.Control.Feedback type="invalid">
                    {t('required_field')}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            )}
          />
        </Col>
      </Row>

      {/* charger_pid  */}
      <Row className="mb-2">
        <Col>
          <Controller
            name="charger_pid"
            control={control}
            rules={{
              validate: value =>
                value !== 0 || t('charger_required_for_planned')
            }}
            render={({ field }) => (
              <Form.Floating>
                <Form.Select
                  {...field}
                  value={field.value}
                  onChange={e => field.onChange(Number(e.target.value))}
                  isInvalid={!!errors.charger_pid}
                >
                  <option value={0} className="d-none">
                    {''}
                  </option>
                  {chargers.map(user => (
                    <option key={user.pid} value={user.pid}>
                      {user.name}
                    </option>
                  ))}
                </Form.Select>
                <label>{t('charger_pid')}</label>
                {errors.charger_pid && (
                  <Form.Control.Feedback type="invalid">
                    {t('required_field')}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            )}
          />
        </Col>
      </Row>

      {/* status_pid */}
      {/* {selectedOrderPlanId && (
        <Row className="mb-2">
          <Col>
            <Controller
              name="status_pid"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field} isInvalid={!!errors.status_pid}>
                    <option className="d-none" value="">
                      {t('select_status')}
                    </option>
                    {statusCodes.map((code, idx) => (
                      <option key={idx + 1} value={idx + 1}>
                        {t(code)}
                      </option>
                    ))}
                  </Form.Select>
                  <label>{t('status')}</label>
                  {errors.status_pid && (
                    <Form.Control.Feedback type="invalid">
                      {t('status_required')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>
      )} */}

      {/* visit_date */}
      <Row className="mb-2">
        <Col>
          <Controller
            name="visit_date"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating>
                <Flatpickr
                  {...field}
                  value={field.value ?? []}
                  onChange={dates => field.onChange(dates)}
                  className={classNames('form-control', {
                    'is-invalid': !!errors.visit_date
                  })}
                  options={{
                    dateFormat: 'M j, Y',
                    minDate: 'today',
                    locale: getFlatpickrLocale(),
                    disableMobile: true
                  }}
                />
                <label>{t('visit_date')}</label>
                {errors.visit_date && (
                  <Form.Control.Feedback type="invalid">
                    {t('required_field')}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            )}
          />
        </Col>
      </Row>

      {/* Submit */}
      <div className="d-flex justify-content-end mt-auto">
        <Button
          style={{ height: '48px', width: '100%' }}
          variant="primary"
          type="submit"
        >
          {t(selectedOrderPlanId ? 'save' : 'add')}
        </Button>
      </div>
    </Form>
  );
};

export default CreateOrderPlan;
